<template>
  <div class="orgs-switcher">
    <!-- Search (shown if > 4 organizations) -->
    <div v-if="hasSearch" class="orgs-switcher__search">
      <v-text-field
        v-model="searchQuery"
        :placeholder="$t('organizations.searchPlaceholder')"
        variant="solo-filled"
        density="compact"
        flat
        hide-details
        :prepend-inner-icon="mdiMagnify"
        clearable
        class="orgs-switcher__search-input"
      />
    </div>

    <!-- Active organization (always first) -->
    <div v-if="activeOrganization && !loading" class="orgs-switcher__section">
      <div class="orgs-switcher__section-label">
        {{ $t('organizations.current') }}
      </div>
      <div class="orgs-switcher__active-card">
        <div class="orgs-switcher__active-card-content">
          <v-avatar :color="getAvatarColor(activeOrganization.org_id)" size="44" rounded="lg">
            <span class="text-subtitle-2 font-weight-bold">
              {{ getInitials(activeOrganization.org_name) }}
            </span>
          </v-avatar>
          <div class="orgs-switcher__active-info">
            <div class="orgs-switcher__active-name">{{ activeOrganization.org_name }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Other organizations -->
    <div v-if="otherOrganizations.length > 0 || loading" class="orgs-switcher__section">
      <div class="orgs-switcher__section-label">
        {{ $t('organizations.switchTo') }}
      </div>

      <div class="orgs-switcher__list">
        <template v-if="loading">
          <div v-for="i in 2" :key="i" class="orgs-switcher__skeleton">
            <v-skeleton-loader type="avatar" />
            <div class="orgs-switcher__skeleton-text">
              <v-skeleton-loader type="text" />
            </div>
          </div>
        </template>

        <template v-else>
          <button
            v-for="org in filteredOrganizations"
            :key="org.org_id"
            class="orgs-switcher__item"
            :disabled="switchingTo === org.org_id"
            @click="handleSwitch(org)"
          >
            <v-avatar :color="getAvatarColor(org.org_id)" size="36" rounded="lg">
              <span class="text-caption font-weight-bold">
                {{ getInitials(org.org_name) }}
              </span>
            </v-avatar>
            <div class="orgs-switcher__item-info">
              <span class="orgs-switcher__item-name">{{ org.org_name }}</span>
            </div>
            <v-progress-circular
              v-if="switchingTo === org.org_id"
              indeterminate
              size="18"
              width="2"
              color="primary"
            />
            <v-icon
              v-else
              :icon="mdiArrowRight"
              size="18"
              class="orgs-switcher__item-arrow"
            />
          </button>

          <!-- Empty state if search has no results -->
          <div v-if="searchQuery && filteredOrganizations.length === 0" class="orgs-switcher__empty">
            <v-icon :icon="mdiMagnify" size="32" class="orgs-switcher__empty-icon" />
            <span>{{ $t('organizations.noResults') }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Empty state if no organizations -->
    <div v-if="!loading && (!organizations || organizations.length === 0)" class="orgs-switcher__empty-state">
      <v-icon :icon="mdiDomain" size="48" class="orgs-switcher__empty-icon" />
      <p class="orgs-switcher__empty-text">{{ $t('organizations.noOrganizations') }}</p>
    </div>

    <!-- Create CTA -->
    <div v-if="canCreate" class="orgs-switcher__create">
      <button
        class="orgs-switcher__create-btn"
        :disabled="!!createDisabledReason"
        @click="handleCreateOrganization"
      >
        <div class="orgs-switcher__create-icon">
          <v-icon :icon="mdiPlus" size="20" />
        </div>
        <div class="orgs-switcher__create-text">
          <span class="orgs-switcher__create-title">{{ $t('organizations.createNew') }}</span>
          <span class="orgs-switcher__create-subtitle">
            {{ createDisabledReason || $t('organizations.createSubtitle') }}
          </span>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  mdiPlus,
  mdiMagnify,
  mdiArrowRight,
  mdiDomain,
} from '@mdi/js'
import { getInitials, getColorFromUid } from '../../core/utils'
import type { MembershipSummary } from '@/api/session/access'
import { useOrganization } from '../composables/useOrganization'
import { useOrganizationSwitcher } from '../composables/useOrganizationSwitcher'

interface Props {
  showTitle?: boolean
  /** The caller's memberships, as `GET /api/app/access` lists them. */
  organizations?: MembershipSummary[]
  loading?: boolean
  /** Whether to offer founding a new organization. */
  canCreate?: boolean
  /** Why founding is offered but refused right now; greys the button and replaces its subtitle. */
  createDisabledReason?: string
}

const props = withDefaults(defineProps<Props>(), {
  showTitle: true,
  loading: false,
  canCreate: false,
})

const emit = defineEmits<{
  'close-menu': []
  'create-organization': []
}>()

const route = useRoute()
const router = useRouter()
const { organizationId: currentOrgId } = useOrganization()
const { startSwitching } = useOrganizationSwitcher()

const searchQuery = ref('')
const switchingTo = ref<string | null>(null)

const hasSearch = computed(() => (props.organizations?.length ?? 0) > 4)

const activeOrganization = computed(() => {
  const activeId = (route.params.organizationId as string) || currentOrgId.value
  return props.organizations?.find((org) => org.org_id === activeId)
})

const otherOrganizations = computed(() => {
  const activeId = (route.params.organizationId as string) || currentOrgId.value
  return props.organizations?.filter((org) => org.org_id !== activeId) ?? []
})

const filteredOrganizations = computed(() => {
  if (!searchQuery.value) return otherOrganizations.value
  const query = searchQuery.value.toLowerCase()
  return otherOrganizations.value.filter((org) => org.org_name.toLowerCase().includes(query))
})

const getAvatarColor = (uid: string) => getColorFromUid(uid)

const handleSwitch = async (org: MembershipSummary) => {
  switchingTo.value = org.org_id
  emit('close-menu')

  startSwitching({
    uid: org.org_id,
    name: org.org_name,
  })

  await nextTick()
  await new Promise((resolve) => setTimeout(resolve, 50))

  await router.push({
    name: 'home',
    params: { organizationId: org.org_id },
  })

  switchingTo.value = null
}

const handleCreateOrganization = () => {
  emit('create-organization')
  emit('close-menu')
}

</script>

<style scoped lang="scss">
.orgs-switcher {
  display: flex;
  flex-direction: column;

  // Search
  &__search {
    padding: 12px 12px 8px;
  }

  &__search-input {
    :deep(.v-field) {
      background: rgba(var(--v-theme-on-surface), 0.04);
      border-radius: 10px;
      font-size: 0.875rem;
    }
  }

  // Sections
  &__section {
    padding: 8px 0;
  }

  &__section-label {
    padding: 0 16px 8px;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  // Active card
  &__active-card {
    margin: 0 12px;
    padding: 12px;
    background: rgba(var(--v-theme-primary), 0.08);
    border: 1px solid rgba(var(--v-theme-primary), 0.2);
    border-radius: 12px;
  }

  &__active-card-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__active-icon {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__active-info {
    flex: 1;
    min-width: 0;
  }

  &__active-name {
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1.3;
    color: rgb(var(--v-theme-primary));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__active-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  // List items
  &__list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    background: transparent;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    text-align: left;

    &:hover:not(:disabled) {
      background: rgba(var(--v-theme-on-surface), 0.06);

      .orgs-switcher__item-arrow {
        opacity: 1;
        transform: translateX(0);
      }
    }

    &:disabled {
      cursor: wait;
      opacity: 0.7;
    }
  }

  &__item-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__item-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-members {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__item-arrow {
    opacity: 0;
    transform: translateX(-4px);
    transition: all 0.15s ease;
    color: rgba(var(--v-theme-on-surface), 0.4);
  }

  // Skeleton
  &__skeleton {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
  }

  &__skeleton-text {
    flex: 1;
  }

  // Empty states
  &__empty,
  &__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    gap: 8px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.8125rem;
  }

  &__empty-icon {
    opacity: 0.4;
  }

  &__empty-text {
    margin: 0;
  }

  // Create CTA
  &__create {
    padding: 8px 8px 12px;
    margin-top: auto;
  }

  &__create-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    background: transparent;
    border: 1.5px dashed rgba(var(--v-theme-primary), 0.3);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }

    &:hover:not(:disabled) {
      background: rgba(var(--v-theme-primary), 0.04);
      border-color: rgba(var(--v-theme-primary), 0.5);

      .orgs-switcher__create-icon {
        background: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
      }
    }
  }

  &__create-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-radius: 8px;
    color: rgb(var(--v-theme-primary));
    transition: all 0.15s ease;
  }

  &__create-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__create-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
  }

  &__create-subtitle {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }
}
</style>
